/**
 * zalo-label-import-dialog.tsx — Công cụ "Nhập nhãn từ Zalo" nằm trong hộp Thêm nhãn (Nhắn tin → bộ lọc nhãn, và Cài đặt → Quản lý nhãn).
 * Góp ý 09/10 (Wind): lấy nhãn đã gắn ở Zalo PC về eCDP, gộp với nhãn eCDP và gắn đúng cho khách / nhóm đã gắn ở Zalo.
 * Bấm tay, một chiều Zalo → eCDP, có XEM TRƯỚC rồi mới nhập. Chỉ thêm nhãn (không gỡ nhãn đang có), không chạy tự động hoá.
 * Quy tắc gộp / gắn: backend modules/zalo/zalo-label-import.ts. Chữ giải thích: lib/zalo-label-import.ts.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Download, Loader2, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { initials } from '@/lib/utils'
import { useMyPermissions } from '@/hooks/use-settings'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { useZaloLabelImport, useZaloLabelPreview, type ZaloLabelImportResult, type ZaloLabelPreview } from '@/hooks/use-zalo-label-import'
import { assignHeadline, canImport, fetchedAtLabel, importResultLine, labelsHeadline, unmatchedNote } from '@/lib/zalo-label-import'

/**
 * Khối "Công cụ" + nút mở hộp nhập, đặt trong hộp Thêm nhãn (chỉ khi thêm nhãn MỚI). Chỉ hiện với người có quyền tạo nhãn + sửa khách
 * (backend đòi cả hai + quyền với tài khoản Zalo đó).
 */
export function ZaloLabelImportButton() {
  const { t } = useTranslation('convContact')
  const { data: perms } = useMyPermissions()
  const [open, setOpen] = useState(false)
  if (!perms?.has('tags.create') || !perms?.has('contacts.update')) return null
  return (
    <>
      <div className="space-y-1.5 rounded-lg border border-dashed p-3">
        <p className="text-xs font-medium text-muted-foreground">{t('labelImport.tools')}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Download /> {t('labelImport.button')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('labelImport.toolsHint')}</p>
      </div>
      <ZaloLabelImportDialog open={open} onOpenChange={setOpen} />
    </>
  )
}

export function ZaloLabelImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation('convContact')
  const { data: accounts, isLoading } = useZaloAccounts('personal', true, open)
  // Chỉ tài khoản đang kết nối thật (backend cũng đòi đang kết nối).
  const usable = useMemo(() => (accounts ?? []).filter((a) => !a.isDisabled && a.liveStatus === 'connected'), [accounts])
  const [accountId, setAccountId] = useState('')
  const [preview, setPreview] = useState<ZaloLabelPreview | null>(null)
  const [result, setResult] = useState<ZaloLabelImportResult | null>(null)
  const previewMut = useZaloLabelPreview()
  const importMut = useZaloLabelImport()

  // Mở hộp: bắt đầu lại; chỉ có một tài khoản thì chọn sẵn.
  useEffect(() => {
    if (!open) return
    setPreview(null)
    setResult(null)
  }, [open])
  useEffect(() => {
    if (!open) return
    if (usable.length === 1) setAccountId(usable[0].id)
    else if (accountId && !usable.some((a) => a.id === accountId)) setAccountId('')
  }, [open, usable, accountId])

  const busy = previewMut.isPending || importMut.isPending

  function pick(id: string) {
    setAccountId(id)
    setPreview(null)
    setResult(null)
  }

  async function runPreview() {
    try {
      setResult(null)
      setPreview(await previewMut.mutateAsync(accountId))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function runImport() {
    try {
      const r = await importMut.mutateAsync(accountId)
      setResult(r)
      setPreview(null)
      toast.success(importResultLine(r.totals))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const shown = result ?? preview
  const when = fetchedAtLabel(shown?.fetchedAt)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('labelImport.button')}</DialogTitle>
          <DialogDescription>
            {t('labelImport.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('labelImport.accountLabel')}</Label>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">{t('labelImport.loadingAccounts')}</p>
            ) : usable.length === 0 ? (
              <p className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                {t('labelImport.noAccounts')}
              </p>
            ) : (
              <Select value={accountId} onValueChange={pick}>
                <SelectTrigger aria-label={t('labelImport.pickAccount')}>
                  <SelectValue placeholder={t('labelImport.pickAccount')} />
                </SelectTrigger>
                <SelectContent>
                  {usable.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={a.avatarUrl ?? undefined} alt="" />
                          <AvatarFallback className="text-[9px]">{initials(a.displayName)}</AvatarFallback>
                        </Avatar>
                        {a.displayName ?? t('labelImport.noName')}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {shown && (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3 text-sm" data-testid="zalo-label-summary">
              {result && (
                <p className="flex items-start gap-2 font-medium text-success">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {importResultLine(result.totals)}
                </p>
              )}
              <p className="font-medium">{labelsHeadline(shown.totals)}</p>
              {!result && <p>{assignHeadline(shown.totals)}</p>}
              {unmatchedNote(shown.totals) && <p className="text-muted-foreground">{unmatchedNote(shown.totals)}</p>}
              {result && result.skippedOverLimit > 0 && (
                <p className="text-warning">{t('labelImport.overLimit', { count: result.skippedOverLimit })}</p>
              )}
              {shown.tags.length > 0 && (
                <ul className="max-h-52 space-y-1 overflow-y-auto pr-1" aria-label={t('labelImport.tagsAria')}>
                  {shown.tags.map((tag) => (
                    <li key={tag.name} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                      <span className="min-w-0 flex-1 truncate font-medium">{tag.name}</span>
                      <Badge variant={tag.isNew ? 'success' : 'secondary'} className="px-1.5 py-0 text-[10px]">{tag.isNew ? t('labelImport.badgeNew') : t('labelImport.badgeMerged')}</Badge>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {t('labelImport.tagStats', { conversations: tag.conversations, assigned: tag.newAssignments, verb: result ? t('labelImport.assigned') : t('labelImport.willAssign') })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {when && <p className="text-xs text-muted-foreground">{t('labelImport.fetchedAt', { when, cached: shown.cached ? t('labelImport.cachedNote') : '' })}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:actions.close')}
          </Button>
          <Button type="button" variant={preview ? 'outline' : 'default'} onClick={runPreview} disabled={!accountId || busy}>
            {previewMut.isPending && <Loader2 className="animate-spin" />}
            {preview || result ? t('labelImport.again') : t('labelImport.previewBtn')}
          </Button>
          {preview && (
            <Button type="button" onClick={runImport} disabled={busy || !canImport(preview.totals)}>
              {importMut.isPending && <Loader2 className="animate-spin" />}
              {t('labelImport.importBtn')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
